// FRONT-2001: turn-grouping, status, delegation-settle and delegation-queue
// state machine for a GPT-Live-1 call. Pure and clock-injected (every method
// that cares about time takes `now` explicitly rather than calling
// `Date.now()` itself) — same testability style as `voice-turn-gate.ts`:
// framework-free, no WebRTC, no React, so it runs under the plain-node
// vitest runner this repo's frontend tests use. `use-voice-call.ts` feeds it
// every inbound data-channel message via `observe`, ticks it on a 250ms
// interval via `tick`, reports a finished delegation via `delegationDone`,
// and calls `flush`/`stop` at end-of-call — then sends whatever frames the
// returned effects ask for and applies the rest (status, transcripts, a
// delegate fetch) onto the same code paths the realtime switch already uses.
//
// Two rules this file exists to get right (final-plan.md §3 items 5/6):
//   - Turn grouping: a delta from the OTHER speaker closes the open buffer;
//     each speaker also has its own quiet timer (user 1200ms, assistant
//     2500ms) that closes a stalled buffer on its own. The assistant's quiet
//     timer is suppressed while a delegation is queued or in flight, so a
//     filler line ("Sure! Checking...") and the spoken result that follows
//     it once the delegation resolves land as ONE assistant turn, not two.
//   - Delegation settle: a delegation is not resolved the instant it's
//     raised — we hold it until an input delta with `endMs >= offsetMs` or
//     an output delta with `startMs >= offsetMs` has been seen (the words
//     the delegation is ABOUT have actually arrived), or 1500ms have passed
//     regardless. Only one delegation is ever in flight; a second one that
//     settles while the first is still running queues behind it.
//
// The wire contract for what a settled delegation actually sends
// (`{liveSessionId, delegationId, offsetMs, turns, pending?, handled}`) is
// validated server-side in `src/voice/live.ts` — the caps below
// (MAX_TURNS/MAX_TURN_TEXT_CHARS/MAX_TOTAL_TURNS_CHARS/MAX_PENDING_CHARS/
// MAX_HANDLED) mirror that route's own constants so a body built here is
// never the reason a delegate call gets a 400 back.
//
// `pending` precedence (contract detail from the ticket, matching
// `src/voice/live-delegate.ts`'s own resolution rule): the server treats
// `pending` as authoritative the moment the key is present at all, even as
// `""` — so this tracker must never send `""` while `turns` still ends on a
// real, unactioned user line the server hasn't seen yet (that would tell it
// "nothing new" and make it skip straight to repeating the last handled
// result). Three cases, tracked by `userWordsSinceDispatch` (set on EVERY
// input-transcript delta, including one that only extends a buffer already
// open at the PREVIOUS dispatch — cleared the moment a delegation is
// actually dispatched):
//   - An open, non-empty user buffer at dispatch time → `pending` is that
//     text (the words the NEXT delegation is about, already known).
//   - No open buffer, but words arrived since the last dispatch (the buffer
//     closed into `turns` on its own quiet timer before this delegation
//     settled) → `pending` is OMITTED, so the server falls back to that
//     closed turn already sitting at the end of `turns` — a genuine second
//     request must never be swallowed as "nothing new".
//   - No open buffer and no words at all since the last dispatch (including
//     the very first delegation of a call, when nothing has been said yet)
//     → `pending: ""`, telling the server explicitly there is nothing new:
//     repeat the last handled result, or "didn't catch that" when there is
//     none.

import type { VoiceStatus } from './voice-event-router';
import { routeLiveEvent } from './live-event-router';

export interface LiveDelegateTurn {
  role: 'user' | 'assistant';
  text: string;
}

export interface LiveDelegateHandled {
  offsetMs: number;
  content: string;
}

export interface LiveDelegateRequestBody {
  liveSessionId: string;
  delegationId: string;
  offsetMs: number;
  turns: LiveDelegateTurn[];
  pending?: string;
  handled: LiveDelegateHandled[];
  /** R2-3: the session's anchored file/document content, same as the Live
   *  connect call sent — the tracker itself never sets this (it has no
   *  notion of the launching chat's asset), it is spliced on by
   *  `use-voice-call.ts`'s `withAssetContext` right before the request goes
   *  out. Optional, and never sent as an empty string — omitted entirely
   *  when the session has none. */
  assetContext?: string;
  /** FRONT-254: the latest screen note on a hands-free call, spliced on by
   *  `use-voice-call.ts` like `assetContext`. Omitted when there is none. */
  screen?: string;
}

/** What the hook hands back once `POST /api/voice/live/delegate` (or the
 *  40s-abort fallback) has produced spoken content for a delegation. */
export interface LiveDelegationResult {
  content: string;
}

export type LiveTrackerEffect =
  | { kind: 'status'; status: VoiceStatus }
  | { kind: 'user-transcript'; text: string }
  | { kind: 'assistant-delta'; delta: string }
  /** `alreadySaved`: the spoken reading of a late result the server already
   *  wrote into the chat (FRONT-254) — show it, never save it again. */
  | { kind: 'assistant-final'; text: string; alreadySaved?: true }
  | { kind: 'run-delegation'; delegationId: string; body: LiveDelegateRequestBody }
  | { kind: 'send-frame'; frame: Record<string, unknown> }
  | { kind: 'remote-end'; reason?: string }
  | { kind: 'error'; message: string };

// Mirrors src/voice/live.ts's own validation constants exactly — see that
// route's `validateDelegateBody` for the authoritative values.
const MAX_TURNS = 24;
const MAX_TURN_TEXT_CHARS = 1_000;
const MAX_TOTAL_TURNS_CHARS = 6_000;
const MAX_PENDING_CHARS = 1_000;
const MAX_HANDLED = 6;

const USER_QUIET_MS = 1_200;
const ASSISTANT_QUIET_MS = 2_500;
const SETTLE_TIMEOUT_MS = 1_500;
/** A status of "speaking" holds for this long after the last output delta
 *  before falling through to "thinking"/"listening" (final-plan.md §3-14). */
const OUTPUT_RECENCY_MS = 900;
/** FRONT-254: how long a late-result signal waits for its reading to start.
 *  The server signals right before its sideband append, whose ack is capped
 *  at 8s; past this the append evidently failed, and the next thing EDDY
 *  says is an ordinary reply that must be saved. */
const LATE_READING_ARM_MS = 12_000;

type Speaker = 'user' | 'assistant';

interface OpenBuffer {
  speaker: Speaker;
  text: string;
  lastActivityAt: number;
  /** FRONT-254: the reading of a late result the server already saved. */
  lateReading?: boolean;
}

interface PendingDelegation {
  id: string;
  offsetMs: number;
  createdAt: number;
  settled: boolean;
}

/** Drop the OLDEST entries first until both caps hold — matching the
 *  server's own "oldest dropped first" rule. Each turn's own text is
 *  truncated to its per-turn cap (kept from the start; in practice a single
 *  live-transcript turn never gets remotely close to 1000 characters, so
 *  the direction is a tie-break, not a real behavior). */
function capTurns(turns: LiveDelegateTurn[]): LiveDelegateTurn[] {
  let capped = turns.map((t) => ({ role: t.role, text: t.text.slice(0, MAX_TURN_TEXT_CHARS) }));
  if (capped.length > MAX_TURNS) capped = capped.slice(capped.length - MAX_TURNS);
  let total = capped.reduce((sum, t) => sum + t.text.length, 0);
  while (total > MAX_TOTAL_TURNS_CHARS && capped.length > 0) {
    total -= capped[0].text.length;
    capped = capped.slice(1);
  }
  return capped;
}

function freshEventId(): string {
  return `live_${crypto.randomUUID()}`;
}

export class LiveCallTracker {
  private started = false;
  private stopped = false;
  private queuedFrames: Record<string, unknown>[] = [];

  private open: OpenBuffer | null = null;
  private readonly closedTurns: LiveDelegateTurn[] = [];

  private lastInputDeltaEndMs: number | null = null;
  private lastOutputDeltaStartMs: number | null = null;
  private lastOutputDeltaWallAt: number | null = null;

  private readonly pendingDelegations: PendingDelegation[] = [];
  private active: { id: string; offsetMs: number } | null = null;
  private handled: LiveDelegateHandled[] = [];
  /** R2-1: true once any input-transcript delta has arrived since the last
   *  delegation was DISPATCHED (not merely settled/queued) — including a
   *  delta that only extends a buffer that was already open at that
   *  dispatch. Cleared the instant `tryDispatchNext` actually dispatches.
   *  See the module doc for the three-way `pending` precedence this drives. */
  private userWordsSinceDispatch = false;

  private lastStatus: VoiceStatus | null = null;

  /** FRONT-254: when the server last said a late result is about to be
   *  spoken, until its reading starts (or the signal expires). */
  private lateReadingArmedAt: number | null = null;

  /** FRONT-2001 round 3 (§7): true the first time `claimEndedSend()` is
   *  called for THIS tracker instance — the once-per-tracker guard behind
   *  POSTing `/api/voice/live/ended`. Lives here, not on call-level state in
   *  `use-voice-call.ts`, because `stop()`'s live branch and `cleanup()`'s
   *  own 'failed'/'disconnected' path (see that function's Review S5
   *  comment) can both run for the very same tracker, and only the tracker
   *  itself persists across that. */
  private endedSent = false;

  constructor(private readonly sessionId: string) {}

  /** The OpenAI-issued session id this tracker was built with — read by
   *  `use-voice-call.ts` to build the `/ended` request body. */
  get liveSessionId(): string {
    return this.sessionId;
  }

  /** True exactly once per tracker instance, false on every call after —
   *  see the `endedSent` field doc above. A brand-new call gets its own
   *  tracker and therefore its own claim. */
  claimEndedSend(): boolean {
    if (this.endedSent) return false;
    this.endedSent = true;
    return true;
  }

  /** Feed every inbound Live data-channel message here. Returns the effects
   *  the hook should apply, in order. A no-op once `stop()` has been called. */
  observe(raw: unknown, now: number): LiveTrackerEffect[] {
    if (this.stopped) return [];
    const action = routeLiveEvent(raw);
    switch (action.kind) {
      case 'started':
        this.started = true;
        return this.flushQueuedFrames();

      case 'input-delta': {
        const effects = this.appendOrOpen('user', action.delta, now);
        this.lastInputDeltaEndMs = action.endMs;
        // R2-1: every input delta counts, even one that only extends a
        // buffer that was already open at the last dispatch — see the
        // module doc's three-way `pending` precedence.
        this.userWordsSinceDispatch = true;
        effects.push(...this.checkSettle(now));
        return effects;
      }

      case 'output-delta': {
        const effects = this.startLateReadingIfDue(now);
        effects.push(...this.appendOrOpen('assistant', action.delta, now));
        effects.push({ kind: 'assistant-delta', delta: action.delta });
        this.lastOutputDeltaStartMs = action.startMs;
        this.lastOutputDeltaWallAt = now;
        effects.push(...this.checkSettle(now));
        return effects;
      }

      case 'delegation':
        this.pendingDelegations.push({
          id: action.delegationId,
          offsetMs: action.offsetMs,
          createdAt: now,
          settled: false,
        });
        return this.checkSettle(now);

      case 'closed': {
        // Review S1: close whatever buffer is still open — and emit that
        // turn effect — BEFORE marking stopped and emitting remote-end.
        // `closeOpenBuffer` doesn't itself check `stopped` (same
        // unconditional close `flush()` uses at ordinary end-of-call), so
        // this must run first: setting `stopped` before this ran the risk
        // of nothing calling `closeOpenBuffer` at all, since the hook's own
        // end-of-call `flush()` (which normally does this) is a no-op once
        // stopped is already true.
        const closeEffects = this.closeOpenBuffer();
        // The server ended the call out from under us. Mark stopped so any
        // stray message still in flight over a closing channel is dropped —
        // the hook decides what to actually DO with a remote end (toast +
        // its own stop path), this only stops US from producing more effects.
        this.stopped = true;
        return [...closeEffects, { kind: 'remote-end', reason: action.reason }];
      }

      case 'error':
        return [{ kind: 'error', message: action.message }];

      case 'commentary-appended':
      case 'instructions-appended':
      case 'muted':
      case 'unmuted':
      case 'ignore':
      default:
        return [];
    }
  }

  /**
   * FRONT-254: the server is about to speak a late result into this call,
   * and has already written it into the chat. The next assistant line that
   * starts fresh — a new buffer, or after a pause in an open one — is that
   * reading, and is marked `alreadySaved`. Speech that runs straight on is
   * never split, so a sentence in progress is never lost from the chat; at
   * worst its reading is saved twice, as before.
   */
  lateResultIncoming(now: number): LiveTrackerEffect[] {
    if (this.stopped) return [];
    this.lateReadingArmedAt = now;
    return [];
  }

  /** Call on a steady interval (250ms in the hook). Closes a stalled buffer
   *  on its own quiet timer, resolves a delegation's 1500ms settle timeout,
   *  and re-evaluates status — emitting a status effect only on change. */
  tick(now: number): LiveTrackerEffect[] {
    if (this.stopped) return [];
    const effects: LiveTrackerEffect[] = [];
    if (this.lateReadingArmedAt !== null && now - this.lateReadingArmedAt >= LATE_READING_ARM_MS) {
      this.lateReadingArmedAt = null;
    }
    effects.push(...this.maybeCloseOnQuiet(now));
    effects.push(...this.checkSettle(now));
    const status = this.computeStatus(now);
    if (status !== this.lastStatus) {
      this.lastStatus = status;
      effects.push({ kind: 'status', status });
    }
    return effects;
  }

  /** The hook calls this once a delegate fetch (or its 40s-abort fallback)
   *  has produced spoken content for the currently in-flight delegation.
   *  Records it into `handled`, sends the spoken commentary back, and starts
   *  the next queued delegation if one is waiting. Dropped once stopped —
   *  a result racing in after the call ended has nothing left to speak to. */
  delegationDone(result: LiveDelegationResult, now: number): LiveTrackerEffect[] {
    if (this.stopped || this.active === null) return [];
    const { id, offsetMs } = this.active;
    this.active = null;

    // Review S7: at real resolver latency (8-10s), the assistant's own
    // 2500ms quiet timer has long since run out counting from the filler's
    // LAST WORD — so the very next tick after this closes the filler alone,
    // splitting it from the spoken result that's about to follow into two
    // turns instead of the one the module doc promises. Resetting the open
    // buffer's last-activity time to NOW (the moment the result actually
    // arrived) restarts that timer from here, giving the commentary frame
    // below a full 2500ms to land before the buffer can close on its own.
    if (this.open && this.open.speaker === 'assistant') {
      this.open.lastActivityAt = now;
    }

    this.handled = [...this.handled, { offsetMs, content: result.content }];
    if (this.handled.length > MAX_HANDLED) {
      this.handled = this.handled.slice(this.handled.length - MAX_HANDLED);
    }

    const frame = {
      type: 'session.commentary.append',
      event_id: freshEventId(),
      delegation_id: id,
      content: result.content,
    };
    const effects = this.emitFrame(frame);
    effects.push(...this.tryDispatchNext());
    return effects;
  }

  /** Finalize whatever buffer is still open, unconditionally — no quiet
   *  timer, no suppression check — because the call is ending and there is
   *  no more traffic coming to close it naturally. Called before the hook
   *  copies its turns array for the end-of-call callback. */
  flush(now: number): LiveTrackerEffect[] {
    void now;
    if (this.stopped) return [];
    return this.closeOpenBuffer();
  }

  /** Marks the tracker terminated: `observe`/`tick`/`delegationDone` become
   *  no-ops from here on, so a result racing in after the hook has already
   *  torn the call down is dropped rather than acted on. */
  stop(): void {
    this.stopped = true;
  }

  /** Review S5 / diagnosability: lets a caller tell "a delegation result
   *  arrived after this tracker was stopped" (drop, log it) apart from any
   *  other reason `delegationDone` might return no effects. */
  /** Is EDDY's voice playing right now (output within the last moment)? */
  isSpeaking(now: number): boolean {
    return this.computeStatus(now) === 'speaking';
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  /** Build the mute frame, sending it now if `started` has already been
   *  observed or queuing it otherwise (see `emitFrame`). */
  requestMute(): LiveTrackerEffect[] {
    if (this.stopped) return [];
    return this.emitFrame({ type: 'session.input_audio.mute', event_id: freshEventId() });
  }

  requestUnmute(): LiveTrackerEffect[] {
    if (this.stopped) return [];
    return this.emitFrame({ type: 'session.input_audio.unmute', event_id: freshEventId() });
  }

  // ── internals ────────────────────────────────────────────────────────

  /** FRONT-254: on an output delta while a late-result signal is armed —
   *  open the reading's own buffer when this delta starts a fresh line. */
  private startLateReadingIfDue(now: number): LiveTrackerEffect[] {
    if (this.lateReadingArmedAt === null) return [];
    const open = this.open;
    if (open && open.speaker === 'assistant' && now - open.lastActivityAt < OUTPUT_RECENCY_MS) return [];
    this.lateReadingArmedAt = null;
    const effects = this.closeOpenBuffer();
    this.open = { speaker: 'assistant', text: '', lastActivityAt: now, lateReading: true };
    return effects;
  }

  private appendOrOpen(speaker: Speaker, text: string, now: number): LiveTrackerEffect[] {
    const effects: LiveTrackerEffect[] = [];
    if (this.open && this.open.speaker !== speaker) {
      effects.push(...this.closeOpenBuffer());
    }
    if (!this.open) {
      this.open = { speaker, text: '', lastActivityAt: now };
    }
    this.open.text += text;
    this.open.lastActivityAt = now;
    return effects;
  }

  /** Unconditionally close whatever buffer is open (if any), recording it
   *  into `closedTurns` and emitting the matching final effect. Used both by
   *  a cross-speaker delta (no suppression applies there) and by `flush`. */
  private closeOpenBuffer(): LiveTrackerEffect[] {
    const open = this.open;
    if (!open) return [];
    this.open = null;
    const text = open.text.trim();
    if (!text) return [];
    this.closedTurns.push({ role: open.speaker, text });
    if (open.speaker === 'user') return [{ kind: 'user-transcript', text }];
    return open.lateReading ? [{ kind: 'assistant-final', text, alreadySaved: true }] : [{ kind: 'assistant-final', text }];
  }

  /** Close the open buffer on its own quiet timer. The assistant's timer is
   *  suppressed while a delegation is queued or in flight — see the module
   *  doc for why that's what merges filler + spoken result into one turn. */
  private maybeCloseOnQuiet(now: number): LiveTrackerEffect[] {
    if (!this.open) return [];
    const quietMs = this.open.speaker === 'user' ? USER_QUIET_MS : ASSISTANT_QUIET_MS;
    if (now - this.open.lastActivityAt < quietMs) return [];
    if (this.open.speaker === 'assistant' && this.hasPendingDelegation()) return [];
    return this.closeOpenBuffer();
  }

  private hasPendingDelegation(): boolean {
    return this.active !== null || this.pendingDelegations.length > 0;
  }

  /** Re-evaluate every not-yet-settled delegation against the latest known
   *  transcript position and the 1500ms timeout, then try to dispatch
   *  whatever is settled and not already running. */
  private checkSettle(now: number): LiveTrackerEffect[] {
    for (const d of this.pendingDelegations) {
      if (d.settled) continue;
      const inputOk = this.lastInputDeltaEndMs !== null && this.lastInputDeltaEndMs >= d.offsetMs;
      const outputOk = this.lastOutputDeltaStartMs !== null && this.lastOutputDeltaStartMs >= d.offsetMs;
      const timedOut = now - d.createdAt >= SETTLE_TIMEOUT_MS;
      if (inputOk || outputOk || timedOut) d.settled = true;
    }
    return this.tryDispatchNext();
  }

  /** Dispatch the earliest settled, not-yet-dispatched delegation — but only
   *  when nothing else is currently in flight (one delegation in flight per
   *  call, per the contract). The request body is snapshotted HERE, at
   *  actual dispatch time, not when the delegation first settled — so a
   *  delegation that had to wait in queue picks up anything that finished
   *  (into `handled`) or was said (into `turns`) while it waited. */
  private tryDispatchNext(): LiveTrackerEffect[] {
    if (this.active !== null) return [];
    const idx = this.pendingDelegations.findIndex((d) => d.settled);
    if (idx === -1) return [];
    const [d] = this.pendingDelegations.splice(idx, 1);
    this.active = { id: d.id, offsetMs: d.offsetMs };
    const body = this.buildRequestBody(d.id, d.offsetMs);
    // R2-1: cleared at the moment of actual dispatch (not settle/queue) —
    // the NEXT delegation's body reflects only words that arrived AFTER
    // this one went out.
    this.userWordsSinceDispatch = false;
    return [{ kind: 'run-delegation', delegationId: d.id, body }];
  }

  private buildRequestBody(delegationId: string, offsetMs: number): LiveDelegateRequestBody {
    const turns: LiveDelegateTurn[] = [...this.closedTurns];
    const openText = this.open?.text.trim() ?? '';
    if (this.open && this.open.speaker === 'assistant' && openText) {
      turns.push({ role: 'assistant', text: openText });
    }
    // R2-1: three-way precedence — see the module doc.
    let pendingText: string | undefined;
    if (this.open && this.open.speaker === 'user' && openText) {
      pendingText = openText.slice(0, MAX_PENDING_CHARS);
    } else if (this.userWordsSinceDispatch) {
      pendingText = undefined; // real second request — never swallowed as "nothing new"
    } else {
      pendingText = ''; // genuinely nothing new since the last dispatch
    }
    return {
      liveSessionId: this.liveSessionId,
      delegationId,
      offsetMs,
      turns: capTurns(turns),
      ...(pendingText !== undefined ? { pending: pendingText } : {}),
      handled: this.handled.slice(-MAX_HANDLED),
    };
  }

  private computeStatus(now: number): VoiceStatus {
    const outputRecent =
      this.lastOutputDeltaWallAt !== null && now - this.lastOutputDeltaWallAt < OUTPUT_RECENCY_MS;
    if (outputRecent) return 'speaking';
    if (this.hasPendingDelegation()) return 'thinking';
    return 'listening';
  }

  /** Send a frame now if `started` has already been observed, or hold it
   *  until it has (final-plan.md §3: "Frames sent before started are
   *  queued"). */
  private emitFrame(frame: Record<string, unknown>): LiveTrackerEffect[] {
    if (this.started) return [{ kind: 'send-frame', frame }];
    this.queuedFrames.push(frame);
    return [];
  }

  private flushQueuedFrames(): LiveTrackerEffect[] {
    const frames = this.queuedFrames;
    this.queuedFrames = [];
    return frames.map((frame) => ({ kind: 'send-frame', frame }) as const);
  }
}
